// components/dropdown-menu/DropdownMenu.js
import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./DropdownMenu.scss";

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export default function DropdownMenu({
  items = [], selectedKey, onSelect, onOpen,
  customBtn = null, triggerLabel = "Options", triggerIcon = null,
  disabled = false, hasCaret = true, buttonTitle,
  openOn = "click", closeDelay = 250, closeOnSelect = true,
  direction = "auto", align = "end", gap = 6, pad = 10,
  minWidth = 160, maxWidth = 280, maxHeight = 320, itemHeight = 36,
  matchTriggerWidth = false, usePortal = true, portalTarget = null,
  className = "", triggerClassName = "", menuClassName = "", itemClassName = "",
  style, triggerStyle, menuStyle, itemStyle, textStyle,
  renderItem, selectedIndicator = null, emptyLabel = "No options",
}) {
  const id = useId();
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimer = useRef(null);
  const openedByHover = useRef(false);
  const focusOnOpen = useRef(null);
  const search = useRef({ text: "", time: 0 });
  const [open, setOpen] = useState(false);
  const isOpen = open && !disabled;

  const resolvedItems = useMemo(() => (items || []).filter(Boolean).map((item, index) => ({
    ...item, key: String(item.key ?? index), raw: item,
  })), [items]);

  const clearTimer = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };

  const closeMenu = (restoreFocus = false) => {
    clearTimer();
    focusOnOpen.current = null;
    openedByHover.current = false;
    search.current = { text: "", time: 0 };
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus({ preventScroll: true });
  };

  const enabledButtons = () => Array.from(
    menuRef.current?.querySelectorAll("[data-dropdown-item]:not(:disabled)") || []
  );

  const focusItem = (where) => {
    const buttons = enabledButtons();
    const selected = buttons.find(button => button.getAttribute("aria-checked") === "true");
    const button = where === "last" ? buttons[buttons.length - 1] : selected || buttons[0];
    (button || menuRef.current)?.focus({ preventScroll: true });
    button?.scrollIntoView({ block: "nearest" });
  };

  const openMenu = (focus = null) => {
    if (disabled) return;
    clearTimer();
    if (focus) openedByHover.current = false;
    if (isOpen) {
      if (focus) focusItem(focus);
      return;
    }
    focusOnOpen.current = focus;
    setOpen(true);
    onOpen?.();
  };

  const handlePointerEnter = (event) => {
    if (openOn !== "hover" || event.pointerType !== "mouse") return;
    if (!isOpen) openedByHover.current = true;
    openMenu(); // Hover opens without stealing keyboard focus.
  };

  const handlePointerLeave = (event) => {
    if (openOn !== "hover" || event.pointerType !== "mouse") return;
    clearTimer();
    closeTimer.current = setTimeout(() => {
      // Keep a menu being used with the keyboard open.
      if (!menuRef.current?.contains(document.activeElement)) closeMenu();
    }, closeDelay);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  // Measure the actual menu, including custom content, rather than guessing its height.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    const updatePosition = () => {
      const rect = trigger.getBoundingClientRect();
      const viewportW = document.documentElement.clientWidth;
      const viewportH = window.innerHeight;
      const widthLimit = Math.max(0, Math.min(maxWidth, viewportW - pad * 2));
      menu.style.maxWidth = `${widthLimit}px`;
      menu.style.minWidth = `${Math.min(widthLimit, Math.max(minWidth, matchTriggerWidth ? rect.width : 0))}px`;

      const border = menu.offsetHeight - menu.clientHeight;
      const desiredHeight = Math.min(maxHeight, menu.scrollHeight + border);
      const below = Math.max(0, viewportH - rect.bottom - gap - pad);
      const above = Math.max(0, rect.top - gap - pad);
      const up = direction === "up" || (direction === "auto" && below < desiredHeight && above > below);
      menu.style.maxHeight = `${Math.max(0, Math.min(maxHeight, up ? above : below))}px`;

      const { width, height } = menu.getBoundingClientRect();
      const left = align === "start" ? rect.left
        : align === "center" ? rect.left + (rect.width - width) / 2 : rect.right - width;
      menu.style.left = `${clamp(left, pad, viewportW - width - pad)}px`;
      menu.style.top = `${clamp(up ? rect.top - gap - height : rect.bottom + gap, pad, viewportH - height - pad)}px`;
      menu.style.visibility = "visible";
    };

    updatePosition();
    if (focusOnOpen.current) {
      focusItem(focusOnOpen.current);
      focusOnOpen.current = null;
    }
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updatePosition) : null;
    observer?.observe(trigger);
    observer?.observe(menu);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, resolvedItems, minWidth, maxWidth, maxHeight, matchTriggerWidth, direction, align, gap, pad, usePortal, portalTarget]);

  useEffect(() => {
    if (!isOpen) return;
    const inside = target => triggerRef.current?.contains(target) || menuRef.current?.contains(target);
    const handleOutside = event => { if (!inside(event.target)) closeMenu(); };
    const handleEscape = event => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      closeMenu(inside(document.activeElement));
    };
    document.addEventListener("pointerdown", handleOutside, true);
    document.addEventListener("focusin", handleOutside);
    document.addEventListener("keydown", handleEscape, true);
    return () => {
      document.removeEventListener("pointerdown", handleOutside, true);
      document.removeEventListener("focusin", handleOutside);
      document.removeEventListener("keydown", handleEscape, true);
    };
  }, [isOpen]);

  const handleTriggerKeyDown = event => {
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      openMenu(event.key === "ArrowUp" ? "last" : "first");
    }
  };

  const handleMenuKeyDown = event => {
    event.stopPropagation();
    if (event.key === "Tab") {
      // Resume the page's normal Tab order from the trigger, even through a portal.
      closeMenu(true);
      return;
    }
    const buttons = enabledButtons();
    if (!buttons.length) return;
    const current = buttons.indexOf(document.activeElement);
    let next;
    if (event.key === "ArrowDown") next = (current + 1) % buttons.length;
    if (event.key === "ArrowUp") next = current < 0 ? buttons.length - 1 : (current - 1 + buttons.length) % buttons.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = buttons.length - 1;

    // Type a label (or repeat one letter) to jump to a matching item.
    if (event.key.length === 1 && event.key !== " " && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const now = Date.now();
      const value = (now - search.current.time < 500 ? search.current.text : "") + event.key.toLowerCase();
      search.current = { text: value, time: now };
      const query = [...value].every(char => char === value[0]) ? value[0] : value;
      for (let step = 1; step <= buttons.length; step++) {
        const index = (current + step) % buttons.length;
        if (buttons[index].dataset.label.startsWith(query)) { next = index; break; }
      }
    }
    if (next !== undefined) {
      event.preventDefault();
      buttons[next].focus({ preventScroll: true });
      buttons[next].scrollIntoView({ block: "nearest" });
    }
  };

  const selectItem = item => {
    if (item.disabled) return;
    // Restore focus before callbacks, so a callback may safely focus a dialog/input.
    if (closeOnSelect) closeMenu(true);
    onSelect?.(item.key, item.raw);
    item.onPress?.();
  };

  const sharedStyle = { "--dm-item-height": `${itemHeight}px`, ...style };
  const menu = isOpen ? (
    <div
      id={`${id}-menu`} ref={menuRef} role="menu" tabIndex={-1}
      aria-labelledby={`${id}-trigger`}
      className={`dm__menu ${menuClassName}`}
      style={{ ...sharedStyle, ...menuStyle, position: "fixed", visibility: "hidden" }}
      onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}
      onKeyDown={handleMenuKeyDown} onClick={event => event.stopPropagation()}
    >
      {resolvedItems.length ? resolvedItems.map(item => {
        const selected = selectedKey != null && String(selectedKey) === item.key;
        const state = { selected, disabled: !!item.disabled };
        const label = String(item.textValue ?? (typeof item.label === "string" ? item.label : ""));
        return (
          <button
            key={item.key} type="button" data-dropdown-item data-label={label.toLowerCase()}
            role={selectedKey != null ? "menuitemradio" : "menuitem"}
            aria-checked={selectedKey != null ? selected : undefined}
            aria-label={item.textValue} disabled={!!item.disabled} tabIndex={-1} title={item.title}
            className={`dm__item ${selected ? "is-selected" : ""} ${itemClassName} ${item.className || ""}`}
            style={{ ...itemStyle, ...item.style }}
            onClick={() => selectItem(item)}
          >
            {renderItem ? renderItem(item.raw, state) : <>
              {item.icon != null && <span className="dm__icon" aria-hidden="true">{item.icon}</span>}
              <span className="dm__text" style={textStyle}>{item.label ?? ""}</span>
            </>}
            {selectedKey != null && <span className="dm__indicator" aria-hidden="true">
              {selected && (selectedIndicator ?? <span className="dm__dot" />)}
            </span>}
          </button>
        );
      }) : <div className="dm__empty" role="presentation">{emptyLabel}</div>}
    </div>
  ) : null;

  return <div className={`dm ${className}`} style={sharedStyle}>
    <button
      id={`${id}-trigger`} ref={triggerRef} type="button" disabled={disabled}
      aria-label={typeof triggerLabel === "string" ? triggerLabel : undefined}
      aria-haspopup="menu" aria-expanded={isOpen} aria-controls={isOpen ? `${id}-menu` : undefined}
      title={buttonTitle} className={`dm__trigger ${triggerClassName}`} style={triggerStyle}
      onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}
      onKeyDown={handleTriggerKeyDown}
      onClick={event => {
        event.stopPropagation();
        // The first click on a mouse-hovered trigger should keep its menu open.
        if (isOpen && !openedByHover.current) closeMenu();
        else openMenu("first");
      }}
    >
      {customBtn ?? <>
        {triggerIcon != null && <span className="dm__icon" aria-hidden="true">{triggerIcon}</span>}
        <span className="dm__text" style={textStyle}>{triggerLabel}</span>
      </>}
      {hasCaret && <span className="dm__caret" aria-hidden="true" />}
    </button>
    {menu && (usePortal ? createPortal(menu, portalTarget || document.body) : menu)}
  </div>;
}
